import { Link } from "react-router-dom";
import { CONTACT } from "../data/content";
import { Mail, Phone, MapPin, Clock, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import Seo, { LOCAL_BUSINESS_LD } from "../components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", business_name: "", subject: "", message: "", consent: false, website_field: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (document.getElementById("calendly-widget-js")) return;
    const s = document.createElement("script");
    s.id = "calendly-widget-js";
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
    const l = document.createElement("link");
    l.id = "calendly-widget-css";
    l.rel = "stylesheet";
    l.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(l);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please provide consent to be contacted."); return; }
    setSubmitting(true);
    try {
      await api.post("/submissions/contact", form);
      setDone(true);
      toast.success("Message received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact — Dallas Business Consulting"
        description="Contact The Mogul Group Advisors — 539 W. Commerce St #8101, Dallas, TX 75208. Call 817-799-7274 or email info@TheMogulAdvisors.com."
        path="/contact"
        jsonLd={LOCAL_BUSINESS_LD}
      />
      <section className="bg-white pt-20 pb-10">
        <div className="container-wide max-w-4xl">
          <div className="overline mb-6">Contact</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="contact-headline">
            Let's talk about your business.
          </h1>
          <p className="mt-6 text-lg text-[#4B5563] leading-relaxed">
            Reach out for a consultation, project inquiry, or general question. {CONTACT.response_time}
          </p>
        </div>
      </section>

      <section className="bg-[#F7F7F8]">
        <div className="container-wide py-16 md:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-white p-8 border border-gray-100">
              <div className="overline mb-4">Get in touch</div>
              <ul className="space-y-4 text-sm">
                <li className="flex gap-3 items-start"><MapPin className="w-4 h-4 mt-1 text-gold" strokeWidth={1.5} /><span>{CONTACT.address_line_1}<br/>{CONTACT.address_line_2}</span></li>
                <li className="flex gap-3 items-center"><Mail className="w-4 h-4 text-gold" strokeWidth={1.5} /><a href={`mailto:${CONTACT.email}`} className="hover:text-gold" data-testid="contact-email-link">{CONTACT.email}</a></li>
                <li className="flex gap-3 items-center"><Phone className="w-4 h-4 text-gold" strokeWidth={1.5} /><a href={CONTACT.phone_href} className="hover:text-gold" data-testid="contact-phone-link">{CONTACT.phone}</a></li>
                <li className="flex gap-3 items-center"><Clock className="w-4 h-4 text-gold" strokeWidth={1.5} /><span>{CONTACT.hours}</span></li>
              </ul>
              <div className="mt-8">
                <Link to="/growth-assessment" className="btn btn-primary w-full justify-center" data-testid="contact-assessment-cta">Get Your Free Growth Assessment</Link>
              </div>
            </div>
            <div className="bg-white p-2 border border-gray-100">
              <iframe
                title="Dallas service area map"
                src="https://www.google.com/maps?q=Dallas,TX&output=embed"
                className="w-full h-72 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="lg:col-span-7 bg-white p-8 md:p-10 border border-gray-100">
            {done ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="overline mb-2">Thank you</div>
                <h2 className="font-serif text-2xl md:text-3xl leading-tight">Your message has been received.</h2>
                <p className="mt-4 text-[#4B5563]">We will follow up within one business day.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Full name" required>
                    <input required data-testid="contact-input-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Email" required>
                    <input required type="email" data-testid="contact-input-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="contact-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Business name">
                    <input data-testid="contact-input-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className={inputCls} />
                  </Field>
                </div>
                <Field label="Subject">
                  <input data-testid="contact-input-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Message" required>
                  <textarea required rows={6} data-testid="contact-input-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                </Field>

                {/* honeypot */}
                <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px" }} value={form.website_field} onChange={(e) => setForm({ ...form, website_field: e.target.value })} />

                <label className="flex gap-3 items-start text-sm">
                  <input type="checkbox" required data-testid="contact-input-consent" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1" />
                  <span className="text-[#4B5563]">I agree to be contacted regarding my inquiry. My information will only be used for that purpose.</span>
                </label>
                <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn btn-primary">
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* SCHEDULE DIRECTLY */}
      <section id="schedule" className="bg-white scroll-mt-24" data-testid="contact-calendly-section">
        <div className="container-wide py-16 md:py-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="overline mb-3 flex items-center justify-center gap-2"><Calendar className="w-3.5 h-3.5" strokeWidth={2} /> Or Book Directly</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">Prefer to pick a time now?</h2>
              <p className="mt-4 text-[#4B5563] leading-relaxed max-w-2xl mx-auto">
                Skip the form and book a 30-minute consultation with Alex directly. Same conversation, faster start.
              </p>
            </div>
            <div
              className="calendly-inline-widget bg-white border border-gray-100"
              data-url="https://calendly.com/themoguladvisors-info/30min?hide_gdpr_banner=1&primary_color=c19b54"
              data-testid="contact-calendly-widget"
              style={{ minWidth: "320px", height: "720px" }}
            />
            <p className="mt-4 text-xs text-[#6B7280] text-center">
              Can't see the calendar? <a href="https://calendly.com/themoguladvisors-info/30min" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Open scheduler in a new tab →</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-b border-gray-300 focus:border-gold focus:outline-none py-2 text-[#0A0A0A] placeholder:text-[#9CA3AF]";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-[#6B7280] mb-2">{label}{required && <span className="text-gold"> *</span>}</span>
      {children}
    </label>
  );
}
